// src/storage/geometryStorage.ts — Local-First Persistence for Tactile Geometry (NFR-DATA-001)
// Tracks angle mastery, reflection puzzles solved, and streaks.

export interface GeometryChallengeAttempt {
  tool: 'protractor' | 'mirror' | 'sonar';
  targetValue: number | string;
  actualValue: number | string;
  correct: boolean;
  timestamp: number;
}

export interface GeometryProgressData {
  attempts: GeometryChallengeAttempt[];
  anglesSolved: number;
  reflectionsSolved: number;
  bestAngleStreak: number;
  currentStreak: number;
  lastSessionDate: string;
}

const STORAGE_KEY = 'clawey_geometry_progress_v1';

function getDefaultProgress(): GeometryProgressData {
  return {
    attempts: [],
    anglesSolved: 0,
    reflectionsSolved: 0,
    bestAngleStreak: 0,
    currentStreak: 0,
    lastSessionDate: new Date().toISOString().split('T')[0] ?? '',
  };
}

export class GeometryStorage {
  /** Load saved geometry progress */
  static load(): GeometryProgressData {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return getDefaultProgress();
      const parsed = JSON.parse(raw) as Partial<GeometryProgressData>;
      return {
        ...getDefaultProgress(),
        ...parsed,
        attempts: Array.isArray(parsed.attempts) ? [...parsed.attempts] : [],
      };
    } catch (err) {
      console.warn('Failed to load geometry progress:', err);
      return getDefaultProgress();
    }
  }

  /** Save geometry progress */
  static save(data: GeometryProgressData): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (err) {
      console.warn('Failed to save geometry progress:', err);
    }
  }

  /** Record an attempt */
  static recordAttempt(attempt: GeometryChallengeAttempt): GeometryProgressData {
    const data = this.load();
    data.attempts.push(attempt);

    if (attempt.correct) {
      data.currentStreak += 1;
      if (data.currentStreak > data.bestAngleStreak) {
        data.bestAngleStreak = data.currentStreak;
      }
      if (attempt.tool === 'protractor') {
        data.anglesSolved += 1;
      } else if (attempt.tool === 'mirror') {
        data.reflectionsSolved += 1;
      }
    } else {
      data.currentStreak = 0;
    }

    data.lastSessionDate = new Date().toISOString().split('T')[0] ?? '';
    this.save(data);
    return data;
  }

  /** Reset storage (for tests) */
  static clear(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Ignored
    }
  }
}
